import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import SelectedBatchesChips from "./SelectedBatchesChips";
import { getWriteableDate } from "../../utils";
import { TEXT_SMALL, TEXT_TITLE } from "../../style";

export default function DialogAddBatchEvent({ visible, closeDialog, setEvents, branches = [], requestAPI, showToast }) {
    const [form, setForm] = useState({ cancelled: false });
    const [branchBatches, setBranchBatches] = useState([]);
    const [pendingBatchIds, setPendingBatchIds] = useState([]);
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) {
            setForm({ cancelled: false });
            setBranchBatches([]);
            setPendingBatchIds([]);
            setSelectedBatches([]);
        }
    }, [visible]);

    useEffect(() => {
        if (!form.branch_id) {
            setBranchBatches([]);
            setPendingBatchIds([]);
            return;
        }
        requestAPI({
            requestPath: `batch-events/batches?branch_id=${form.branch_id}`,
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setBranchBatches(data);
                    setPendingBatchIds([]);
                }
            },
        });
    }, [form.branch_id, requestAPI]);

    const branchTitleById = useMemo(() => {
        const map = new Map(branches.map((branch) => [branch.id, branch.title]));
        return map;
    }, [branches]);

    const selectedBatchIdSet = useMemo(() => new Set(selectedBatches.map((batch) => batch.id)), [selectedBatches]);

    const availableBatchesForBranch = useMemo(
        () => branchBatches.filter((batch) => !selectedBatchIdSet.has(batch.id)),
        [branchBatches, selectedBatchIdSet],
    );

    const addBatchesFromBranch = useCallback(() => {
        if (!form.branch_id || !pendingBatchIds.length) {
            showToast({ severity: "warn", summary: "Required", detail: "Select at least one batch to add", life: 2000 });
            return;
        }

        const branchTitle = branchTitleById.get(form.branch_id) || "";
        const pendingIdSet = new Set(pendingBatchIds);
        const toAdd = branchBatches
            .filter((batch) => pendingIdSet.has(batch.id) && !selectedBatchIdSet.has(batch.id))
            .map((batch) => ({
                id: batch.id,
                title: batch.title,
                branch_id: form.branch_id,
                branch_title: batch.branch_title || branchTitle,
            }));

        if (!toAdd.length) {
            showToast({ severity: "info", summary: "Already added", detail: "Those batches are already selected", life: 1500 });
            return;
        }

        setSelectedBatches((prev) => [...prev, ...toAdd]);
        setPendingBatchIds([]);
    }, [branchBatches, branchTitleById, form.branch_id, pendingBatchIds, selectedBatchIdSet, showToast]);

    const removeSelectedBatch = useCallback((batchId) => {
        setSelectedBatches((prev) => prev.filter((batch) => batch.id !== batchId));
    }, []);

    const save = useCallback(() => {
        if (!form.title?.trim() || !form.start_at || !form.end_at || !selectedBatches.length) {
            showToast({ severity: "warn", summary: "Required", detail: "Title, times and batches are required", life: 2000 });
            return;
        }

        requestAPI({
            requestPath: "batch-events",
            requestMethod: "POST",
            requestPostBody: {
                title: form.title.trim(),
                description: form.description || null,
                start_at: getWriteableDate({ date: form.start_at }),
                end_at: getWriteableDate({ date: form.end_at }),
                branch_id: form.branch_id || selectedBatches[0]?.branch_id || null,
                batch_ids: selectedBatches.map((batch) => batch.id),
                cancelled: !!form.cancelled,
            },
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed to add event", life: 2000 }),
            onResponseReceieved: (event, responseCode) => {
                if (event && responseCode === 201) {
                    setEvents((prev) => [event, ...(prev || [])]);
                    showToast({ severity: "success", summary: "Added", detail: "Event added", life: 1000 });
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: event?.error || "Failed to add event", life: 2000 });
                }
            },
        });
    }, [closeDialog, form, requestAPI, selectedBatches, setEvents, showToast]);

    return (
        <Dialog
            header="Add Batch Event"
            visible={visible}
            className="w-11 max-h-screen"
            onHide={closeDialog}
            pt={{ headertitle: { className: TEXT_TITLE }, content: { className: "overflow-y-auto" } }}
        >
            <FloatLabel className="mt-4">
                <InputText
                    className="w-full"
                    value={form.title || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    className="w-full"
                    rows={3}
                    value={form.description || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Description</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    options={branches}
                    optionLabel="title"
                    optionValue="id"
                    value={form.branch_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, branch_id: e.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Branch</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <MultiSelect
                    className="w-full"
                    options={availableBatchesForBranch}
                    optionLabel="title"
                    optionValue="id"
                    value={pendingBatchIds}
                    onChange={(e) => setPendingBatchIds(e.value || [])}
                    disabled={loading || !form.branch_id}
                    placeholder="Select batches from this branch"
                />
                <label className={TEXT_SMALL}>Batches</label>
            </FloatLabel>

            <Button
                className="mt-3"
                label="Add Batches"
                icon="pi pi-plus"
                severity="secondary"
                outlined
                disabled={loading || !form.branch_id || !pendingBatchIds.length}
                onClick={addBatchesFromBranch}
            />

            <SelectedBatchesChips batches={selectedBatches} onRemove={removeSelectedBatch} disabled={loading} />

            <FloatLabel className="mt-5">
                <Calendar
                    className="w-full"
                    value={form.start_at}
                    onChange={(e) => setForm((prev) => ({ ...prev, start_at: e.value }))}
                    showTime
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                    showIcon
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Start</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    className="w-full"
                    value={form.end_at}
                    onChange={(e) => setForm((prev) => ({ ...prev, end_at: e.value }))}
                    showTime
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                    showIcon
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>End</label>
            </FloatLabel>

            <div className="flex align-items-center gap-2 mt-4">
                <Checkbox
                    inputId="cancelled_add"
                    checked={!!form.cancelled}
                    onChange={(e) => setForm((prev) => ({ ...prev, cancelled: e.checked }))}
                    disabled={loading}
                />
                <label htmlFor="cancelled_add" className={TEXT_SMALL}>
                    Cancelled
                </label>
            </div>

            <Button className="mt-4" label="Add Event" severity="warning" loading={loading} onClick={save} />
        </Dialog>
    );
}
